import React, { useState } from 'react';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  ClockIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import InteractiveMap from '../components/ui/InteractiveMap';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  company?: string;
}

interface QuickMessage {
  id: string;
  title: string;
  description: string;
  template: string;
}

const ContactsPage: React.FC = () => {
  const [contactForm, setContactForm] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    company: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showQuickMessages, setShowQuickMessages] = useState(false);

  const quickMessages: QuickMessage[] = [
    {
      id: 'price-request',
      title: 'Запрос цены',
      description: 'Узнать стоимость продукции',
      template: 'Здравствуйте! Прошу предоставить прайс-лист на полиэтиленовые трубы для водоснабжения диаметром ___ мм. Объем заказа: ___ м.п. Спасибо!'
    },
    {
      id: 'technical-consult',
      title: 'Техническая консультация',
      description: 'Получить техническую поддержку',
      template: 'Добрый день! Необходима техническая консультация по выбору труб для проекта ___. Рабочее давление: ___ МПа, диаметр: ___ мм. Буду благодарен за рекомендации.'
    },
    {
      id: 'delivery-info',
      title: 'Информация о доставке',
      description: 'Узнать условия доставки',
      template: 'Здравствуйте! Интересует доставка полиэтиленовых труб в г. ___. Объем груза: ___ тонн. Какие условия доставки и сроки? Спасибо!'
    },
    {
      id: 'partnership',
      title: 'Сотрудничество',
      description: 'Предложить партнерство',
      template: 'Добрый день! Наша компания ___ занимается ___. Заинтересованы в долгосрочном сотрудничестве. Можем обсудить условия партнерства?'
    }
  ];

  const contacts = [
    {
      title: 'Производство и головной офис',
      address: 'г. Уральск, ул. Полевая 1Д, нежилое помещение 1',
      region: 'Западно-Казахстанская область, Республика Казахстан',
      phone: '+7 (708) 493-1267',
      email: 'info@kazpolimerplast.kz',
      hours: 'Пн-Пт: 9:00-18:00, Сб-Вс: выходной',
      isPrimary: true
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Формирование сообщения для WhatsApp
    let whatsappMessage = `📩 *Новое сообщение с сайта (Контакты)*\n\n`;
    whatsappMessage += `👤 *Имя:* ${contactForm.name}\n`;
    whatsappMessage += `📧 *Email:* ${contactForm.email}\n`;
    
    if (contactForm.phone) {
      whatsappMessage += `📱 *Телефон:* ${contactForm.phone}\n`;
    }
    
    if (contactForm.company) {
      whatsappMessage += `🏢 *Компания:* ${contactForm.company}\n`;
    }
    
    if (contactForm.subject) {
      const subjectLabels: Record<string, string> = {
        'price': 'Запрос цены и коммерческого предложения',
        'technical': 'Техническая консультация',
        'order': 'Размещение заказа',
        'delivery': 'Вопросы доставки',
        'quality': 'Контроль качества',
        'partnership': 'Партнерство и сотрудничество',
        'other': 'Другое'
      };
      whatsappMessage += `📌 *Тема:* ${subjectLabels[contactForm.subject] || contactForm.subject}\n`;
    }
    
    whatsappMessage += `\n💬 *Сообщение:*\n${contactForm.message}\n`;
    whatsappMessage += `\n⏰ *Время:* ${new Date().toLocaleString('ru-RU')}`;
    whatsappMessage += `\n📄 *Страница:* ${window.location.href}`;

    // Формирование WhatsApp URL
    const phoneNumber = '77089071712'; // Номер без + и пробелов
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Открытие WhatsApp в новой вкладке
    window.open(whatsappUrl, '_blank');
    
    // Показываем успешное сообщение
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setContactForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        company: ''
      });
      
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1000);
  };

  const handleInputChange = (field: keyof ContactForm, value: string) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuickMessage = (template: string) => {
    setContactForm(prev => ({
      ...prev,
      message: template
    }));
    setShowQuickMessages(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900">
      {/* Header */}
      <div className="bg-white dark:bg-secondary-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
              📞 Контакты
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Свяжитесь с <span className="text-primary-600 block sm:inline">ТОО «KazPolimerPlast»</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-secondary-300 max-w-3xl mx-auto">
              Мы готовы ответить на ваши вопросы и обсудить возможности сотрудничества
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Contact Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {contacts.map((contact, index) => (
            <div key={index} className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg p-8 relative">
              {contact.isPrimary && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    Главный офис
                  </span>
                </div>
              )}
              
              <div className="flex items-center mb-6">
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{contact.title}</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-900 dark:text-white font-medium">{contact.address}</p>
                    <p className="text-gray-600 dark:text-secondary-300 text-sm">{contact.region}</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <svg viewBox="0 0 32 32" fill="currentColor" className="h-5 w-5 text-[#25D366] mr-3 flex-shrink-0">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.924 15.924 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.318 22.614c-.39 1.1-1.932 2.014-3.17 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.974-7.826-6.81-8.064-7.126-.228-.316-1.926-2.566-1.926-4.892s1.218-3.47 1.652-3.944c.434-.474.948-.592 1.264-.592.316 0 .632.002.908.016.292.016.682-.11 1.066.814.39.948 1.328 3.234 1.444 3.47.118.236.196.512.04.828-.158.316-.236.512-.474.79-.236.276-.498.616-.71.826-.238.236-.486.492-.208.966.276.474 1.228 2.028 2.638 3.286 1.812 1.618 3.34 2.12 3.814 2.358.474.236.75.198 1.028-.118.276-.316 1.194-1.388 1.512-1.864.316-.474.632-.394 1.066-.236.434.158 2.718 1.282 3.184 1.516.466.236.776.354.892.55.118.196.118 1.136-.272 2.236z"/>
                  </svg>
                  <div>
                    <p className="text-xs text-gray-500 leading-none">WhatsApp</p>
                    <a href="https://wa.me/77089071712" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 font-medium">
                      +7 (708) 907-1712
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 leading-none">Телефон</p>
                    <a href={`tel:+77084931267`} className="text-primary-600 hover:text-primary-700 font-medium">
                      +7 (708) 493-1267
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                  <a href={`mailto:${contact.email}`} className="text-primary-600 hover:text-primary-700 font-medium">
                    {contact.email}
                  </a>
                </div>
                
                <div className="flex items-start">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-600 dark:text-secondary-300 text-sm">{contact.hours}</p>
                </div>
              </div>
            </div>
          ))}
          
          {/* Contact Methods */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* WhatsApp Contact */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <svg viewBox="0 0 32 32" fill="currentColor" className="h-5 w-5 text-[#25D366]">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.924 15.924 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.318 22.614c-.39 1.1-1.932 2.014-3.17 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.974-7.826-6.81-8.064-7.126-.228-.316-1.926-2.566-1.926-4.892s1.218-3.47 1.652-3.944c.434-.474.948-.592 1.264-.592.316 0 .632.002.908.016.292.016.682-.11 1.066.814.39.948 1.328 3.234 1.444 3.47.118.236.196.512.04.828-.158.316-.236.512-.474.79-.236.276-.498.616-.71.826-.238.236-.486.492-.208.966.276.474 1.228 2.028 2.638 3.286 1.812 1.618 3.34 2.12 3.814 2.358.474.236.75.198 1.028-.118.276-.316 1.194-1.388 1.512-1.864.316-.474.632-.394 1.066-.236.434.158 2.718 1.282 3.184 1.516.466.236.776.354.892.55.118.196.118 1.136-.272 2.236z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">WhatsApp</h3>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-700">
                  Быстрое общение через мессенджер
                </p>
                
                <a 
                  href="https://wa.me/77089071712" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center px-4 py-2 bg-[#25D366] text-white font-bold rounded-lg hover:bg-[#20c351] transition-colors"
                >
                  <svg viewBox="0 0 32 32" fill="currentColor" className="h-5 w-5 mr-2">
                    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.924 15.924 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.318 22.614c-.39 1.1-1.932 2.014-3.17 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.974-7.826-6.81-8.064-7.126-.228-.316-1.926-2.566-1.926-4.892s1.218-3.47 1.652-3.944c.434-.474.948-.592 1.264-.592.316 0 .632.002.908.016.292.016.682-.11 1.066.814.39.948 1.328 3.234 1.444 3.47.118.236.196.512.04.828-.158.316-.236.512-.474.79-.236.276-.498.616-.71.826-.238.236-.486.492-.208.966.276.474 1.228 2.028 2.638 3.286 1.812 1.618 3.34 2.12 3.814 2.358.474.236.75.198 1.028-.118.276-.316 1.194-1.388 1.512-1.864.316-.474.632-.394 1.066-.236.434.158 2.718 1.282 3.184 1.516.466.236.776.354.892.55.118.196.118 1.136-.272 2.236z"/>
                  </svg>
                  +7 (708) 907-1712
                </a>
                
                <p className="text-gray-600 text-sm">
                  Доступно 24/7 - просто отправьте сообщение
                </p>
              </div>
            </div>
            
            {/* Phone Contact */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Телефон</h3>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-700">
                  Звоните нам во время работы
                </p>
                
                <a 
                  href="tel:+77084931267" 
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PhoneIcon className="h-5 w-5 mr-2" />
                  +7 (708) 493-1267
                </a>
                
                <p className="text-gray-600 text-sm">
                  Пн-Пт: 9:00-18:00 (Уральск)
                </p>
              </div>
            </div>
          </div>
          
          {/* Production Info */}
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-200 rounded-xl p-8">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-primary-600 text-lg">🏭</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Производство</h3>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Мощность:</span>
                <span className="font-medium">6000 тонн/год</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Линии:</span>
                <span className="font-medium">4 экструзионные</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Диаметры:</span>
                <span className="font-medium">20-1200 мм</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Основано:</span>
                <span className="font-medium">2010</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form and Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 space-y-4 sm:space-y-0">
              <h2 className="text-2xl font-bold text-gray-900">Написать нам</h2>
              <button
                onClick={() => setShowQuickMessages(!showQuickMessages)}
                className="flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium self-start sm:self-auto"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Быстрые сообщения</span>
                <span className="sm:hidden">Шаблоны</span>
              </button>
            </div>
            
            {showQuickMessages && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Выберите шаблон:</h4>
                <div className="grid grid-cols-1 gap-2">
                  {quickMessages.map((msg) => (
                    <button
                      key={msg.id}
                      onClick={() => handleQuickMessage(msg.template)}
                      className="text-left p-3 bg-white hover:bg-primary-50 rounded-lg border border-gray-200 hover:border-primary-200 transition-colors"
                    >
                      <div className="font-medium text-gray-900 text-sm">{msg.title}</div>
                      <div className="text-gray-600 text-xs">{msg.description}</div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowQuickMessages(false)}
                  className="mt-3 text-gray-500 text-sm hover:text-gray-700"
                >
                  Закрыть шаблоны
                </button>
              </div>
            )}
            
            {submitSuccess ? (
              <div className="text-center py-12">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Сообщение отправлено!</h3>
                <p className="text-gray-600 mb-4">Ваше сообщение отправлено в WhatsApp. Мы ответим вам в ближайшее время.</p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Отправить еще одно сообщение
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ваше имя *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Введите ваше имя"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Компания
                    </label>
                    <input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Название компании"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Телефон
                    </label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="+7 (___) ___-__-__"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тема обращения
                  </label>
                  <select
                    value={contactForm.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Выберите тему</option>
                    <option value="price">Запрос цены и коммерческого предложения</option>
                    <option value="technical">Техническая консультация</option>
                    <option value="order">Размещение заказа</option>
                    <option value="delivery">Вопросы доставки</option>
                    <option value="quality">Контроль качества</option>
                    <option value="partnership">Партнерство и сотрудничество</option>
                    <option value="other">Другое</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Сообщение *
                  </label>
                  <textarea
                    required
                    value={contactForm.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Опишите ваш запрос подробно..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить сообщение'}
                </button>
              </form>
            )}
          </div>
          
          {/* Map and Location */}
          <div className="space-y-8">
            {/* Interactive Map */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b">
                <h3 className="text-xl font-bold text-gray-900">Наше местоположение</h3>
                <p className="text-gray-600">г. Уральск, ул. Полевая 1Д, нежилое помещение 1</p>
              </div>
              
              <InteractiveMap
                address="г. Уральск, ул. Полевая 1Д, нежилое помещение 1"
                coordinates={{
                  latitude: 51.2040,
                  longitude: 51.3705
                }}
                companyName="ТОО «KazPolimerPlast»"
                description="Производство полиэтиленовых труб"
                height="h-64"
              />
            </div>
            
            {/* Directions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Как добраться</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🚗 На автомобиле</h4>
                  <p className="text-gray-600 text-sm">
                    Двигаться по ул. Полевая в направлении промышленной зоны. 
                    Здание находится в доме 1Д, нежилое помещение 1.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🚌 Общественный транспорт</h4>
                  <p className="text-gray-600 text-sm">
                    Автобус №12, №15 до остановки "Полевая". 
                    Далее пешком 5 минут.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🏢 Ориентиры</h4>
                  <p className="text-gray-600 text-sm">
                    Рядом с промышленной зоной, вывеска "KazPolimerPlast" 
                    на здании.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Парковка:</span>
                  <span className="text-green-600 font-medium">Бесплатная</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-600">Доступность:</span>
                  <span className="text-green-600 font-medium">Для инвалидов</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsPage;